import type { CurrencyCode } from '../money/currency';
import { Money, type Money as MoneyValue } from '../money/money';
import { DEFAULT_ROUNDING, type RoundingMode } from '../money/rounding';
import type { DiscountConfig, LineItemInput, LineItemResult } from './types';

function assertLineCurrency(line: LineItemInput, currency: CurrencyCode): void {
  if (line.unitPrice.currency !== currency) {
    throw new Error(
      `LineItem ${line.id}: unitPrice currency ${line.unitPrice.currency} ` +
        `does not match invoice currency ${currency}`,
    );
  }
  if (line.discount?.type === 'fixed' && line.discount.amount.currency !== currency) {
    throw new Error(
      `LineItem ${line.id}: fixed discount currency ${line.discount.amount.currency} ` +
        `does not match invoice currency ${currency}`,
    );
  }
}

/**
 * Apply a discount configuration to a base {@link MoneyValue}, returning
 * the non-negative discount amount (never greater than `base`).
 */
export function applyDiscount(
  base: MoneyValue,
  config: DiscountConfig | undefined,
  mode: RoundingMode = DEFAULT_ROUNDING,
): MoneyValue {
  if (!config) return Money.zero(base.currency);

  let discount: MoneyValue;
  if (config.type === 'percentage') {
    const rateRaw = config.rate;
    const rateNum = typeof rateRaw === 'number' ? rateRaw : Number(rateRaw);
    if (!Number.isFinite(rateNum) || rateNum < 0) {
      throw new Error(`Invalid percentage discount rate: ${String(rateRaw)}`);
    }
    if (rateNum === 0) return Money.zero(base.currency);
    // Use the string form to preserve precision for rates like "7.5".
    const fraction = `${rateRaw}e-2`;
    discount = Money.mul(base, fraction, mode);
  } else {
    if (base.currency !== config.amount.currency) {
      throw new Error(
        `applyDiscount: currency mismatch (base=${base.currency}, discount=${config.amount.currency})`,
      );
    }
    if (Money.isNegative(config.amount)) {
      throw new Error('Fixed discount cannot be negative');
    }
    discount = config.amount;
  }

  // Clamp: discount never exceeds base, never goes below zero.
  /* v8 ignore next -- defensive: validation above rejects negative
     inputs, and `Money.mul` of a non-negative base by a non-negative
     rate cannot produce a negative. */
  if (Money.isNegative(discount)) return Money.zero(base.currency);
  if (Money.gt(discount, base)) return base;
  return discount;
}

/**
 * Compute gross, discount, net, and taxable components for a single
 * line. Pure: no I/O, no store access.
 *
 *   gross    = round(quantity × unitPrice)
 *   discount = applyDiscount(gross, line.discount)
 *   net      = gross − discount
 *   taxable  = taxable ? net : 0
 */
export function computeLineItem(
  line: LineItemInput,
  currency: CurrencyCode,
  mode: RoundingMode = DEFAULT_ROUNDING,
): LineItemResult {
  assertLineCurrency(line, currency);
  const qty = line.quantity;
  if (typeof qty === 'number' && (!Number.isFinite(qty) || qty < 0)) {
    throw new Error(`LineItem ${line.id}: invalid quantity ${qty}`);
  }
  if (typeof qty === 'bigint' && qty < 0n) {
    throw new Error(`LineItem ${line.id}: invalid quantity ${qty}`);
  }
  const gross = Money.mul(line.unitPrice, qty, mode);
  const discount = applyDiscount(gross, line.discount, mode);
  const net = Money.sub(gross, discount);
  const taxable = line.taxable ? net : Money.zero(currency);
  return { id: line.id, gross, discount, net, taxable };
}
